import { useMemo } from "react";
import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { useFilterContext, useSelectedView } from "@/app/selectors";
import {
  getChipSelectionKey,
  getEffectiveObjectiveSelection,
  getEffectiveViewSelection,
  getObjectiveChipEntries,
  getOrderedViewChipCodes,
  type ChipEntry,
} from "@/domain/filters";
import { UNCLASSIFIED_CODE } from "@/domain/types";
import { useI18n } from "@/i18n/useI18n";

/** Quick filter chips for the selected view (classification values or objectives). */
export function ChipFilters() {
  const i18n = useI18n();
  const { t } = i18n;
  const { data } = useApp();
  const { selectedRegions } = useUi();
  const dispatch = useUiDispatch();
  const view = useSelectedView();
  const context = useFilterContext();
  const selectionKey = getChipSelectionKey(view);

  const entries = useMemo<ChipEntry[]>(() => {
    if (view.chipDisplay === "objectives") {
      return getObjectiveChipEntries(
        data.dataset.objectives,
        context.availableObjectives,
        data.dataset.items,
        selectedRegions,
        (value, code) => i18n.objectiveLabel(code, value),
      );
    }
    if (view.chipDisplay === "view") {
      return getOrderedViewChipCodes(view, data.dataset.availableViewCodes[view.id]).map(
        (code) => ({
          label:
            code === UNCLASSIFIED_CODE ? t("unclassifiedItems") : i18n.viewValueLabel(view, code),
          codes: [code],
        }),
      );
    }
    return [];
  }, [view, data.dataset, context.availableObjectives, selectedRegions, i18n, t]);

  if (!selectionKey || !entries.length) return null;
  const selected =
    view.chipDisplay === "objectives"
      ? getEffectiveObjectiveSelection(context)
      : getEffectiveViewSelection(context);
  const isActive = (entry: ChipEntry) => entry.codes.some((code) => selected.has(code));
  const activeCount = entries.filter(isActive).length;
  const allActive = entries.every((entry) => entry.codes.every((code) => selected.has(code)));

  const commit = (codes: Set<string>) =>
    dispatch({ type: "setChipSelection", key: selectionKey, codes });
  const toggleAll = () =>
    commit(allActive ? new Set() : new Set(entries.flatMap((entry) => entry.codes)));
  const toggle = (entry: ChipEntry) => {
    const next = new Set(selected);
    if (isActive(entry)) entry.codes.forEach((code) => next.delete(code));
    else entry.codes.forEach((code) => next.add(code));
    commit(next);
  };

  return (
    <div className="objective-chips">
      <Chip
        active={allActive}
        label={`${allActive ? t("hideAll") : t("showAll")} (${activeCount}/${entries.length})`}
        onClick={toggleAll}
      />
      {entries.map((entry) => (
        <Chip
          key={entry.codes.join("|")}
          active={isActive(entry)}
          label={entry.label}
          onClick={() => toggle(entry)}
        />
      ))}
    </div>
  );
}

function Chip({ active, label, onClick }: { active: boolean; label: string; onClick(): void }) {
  return (
    <button
      type="button"
      className={`objective-chip${active ? " active" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="objective-chip-icon">{active ? "✓" : "✕"}</span>
      <span className="objective-chip-label">{label}</span>
    </button>
  );
}
